import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';
import { UserContext } from './UserContext';
import theme, { registerThemes } from './theme';
import { getRegisterBrand, normalizeRegisterTheme } from './registerBranding';

const DEFAULT_BRANDING = { theme: 'default', logoUrl: null, updatedAt: null };
const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  isOrlen: false,
  isBranded: false,
  brand: getRegisterBrand('default'),
  brandingLoading: false,
  refreshBranding: async () => false,
});

export const useBranding = () => useContext(BrandingContext);

export function BrandingProvider({ children }) {
  const { user, sessionToken } = useContext(UserContext);
  const token = sessionToken === undefined ? localStorage.getItem('token') : sessionToken;
  // Bind every result to both the session and the register. Never cache branding globally.
  const sessionKey = user?.database_name && token
    ? JSON.stringify([user.id, user.database_name, token]) : null;
  const currentSession = useRef(sessionKey);
  currentSession.current = sessionKey;
  const requestSequence = useRef(0);
  const [result, setResult] = useState({ key: null, branding: DEFAULT_BRANDING });
  const branding = sessionKey && result.key === sessionKey ? result.branding : DEFAULT_BRANDING;
  const isOrlen = branding.theme === 'orlen';
  const brand = getRegisterBrand(branding.theme);
  const isBranded = brand.key !== 'default';

  const refreshBranding = useCallback(async () => {
    const sequence = ++requestSequence.current;
    if (!sessionKey) {
      setResult({ key: null, branding: DEFAULT_BRANDING });
      return true;
    }
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/settings/branding`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (currentSession.current !== sessionKey || localStorage.getItem('token') !== token || sequence !== requestSequence.current) return false;
      setResult({ key: sessionKey, branding: {
        theme: normalizeRegisterTheme(data?.theme),
        logoUrl: data?.logoUrl || null,
        updatedAt: data?.updatedAt || null,
      } });
      return true;
    } catch (error) {
      if (currentSession.current !== sessionKey || localStorage.getItem('token') !== token || sequence !== requestSequence.current) return false;
      // A failed request for a new register must never display the previous register's identity.
      setResult(previous => previous.key === sessionKey ? previous : { key: sessionKey, branding: DEFAULT_BRANDING });
      return false;
    }
  }, [sessionKey, token]);

  useEffect(() => {
    refreshBranding();
    const handleFocus = () => refreshBranding();
    window.addEventListener('focus', handleFocus);
    window.addEventListener('register-branding-changed', handleFocus);
    return () => {
      // Invalidate in-flight work on cleanup, including StrictMode's effect replay.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ++requestSequence.current;
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('register-branding-changed', handleFocus);
    };
  }, [refreshBranding]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.registerTheme = brand.key;
    const variables = {
      '--brand-primary': brand.primary,
      '--brand-primary-dark': brand.dark,
      '--brand-primary-soft': brand.soft,
      '--brand-soft': brand.soft,
      '--brand-focus': `rgba(${brand.rgb}, 0.28)`,
      '--brand-heading': brand.headingColor,
      '--brand-nav-background': brand.navBackground,
      '--brand-accent': brand.accent,
      '--workspace-background': brand.background,
    };
    Object.entries(variables).forEach(([key, value]) => root.style.setProperty(key, value));
    const favicon = document.querySelector('link[rel="icon"]');
    const browserColor = document.querySelector('meta[name="theme-color"]');
    if (favicon) favicon.setAttribute('href', brand.faviconUrl);
    if (browserColor) browserColor.setAttribute('content', isBranded ? brand.primary : '#0b1220');
    return () => {
      delete root.dataset.registerTheme;
      Object.keys(variables).forEach((key) => root.style.removeProperty(key));
      if (favicon) favicon.setAttribute('href', '/icon.png');
      if (browserColor) browserColor.setAttribute('content', '#0b1220');
    };
  }, [brand, isBranded]);

  return (
    <BrandingContext.Provider value={{ branding, brand, isBranded, isOrlen, refreshBranding, brandingLoading: Boolean(sessionKey && result.key !== sessionKey) }}>
      <ThemeProvider theme={registerThemes[brand.key] || theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </BrandingContext.Provider>
  );
}
